import React, { useState, useEffect } from 'react';
import { 
    Shield, Upload, Check, X, AlertTriangle, Calendar, 
    FileText, User, Car, Loader2, RefreshCw, Download, CheckCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Helper to build document download URL
const getDocumentUrl = (docPath) => {
    if (!docPath) return null;
    // Ensure we have a valid API_URL
    const baseUrl = API_URL || window.location.origin;
    // Clean the path - remove leading slash if present
    const cleanPath = docPath.startsWith('/') ? docPath.substring(1) : docPath;
    // Construct the full URL with proper path separator
    const fullUrl = `${baseUrl}/api/uploads/${cleanPath}`;
    console.log('[ComplianceTab] getDocumentUrl:', { docPath, baseUrl, cleanPath, fullUrl });
    return fullUrl;
};

// Uploaded file indicator component
const UploadedFileIndicator = ({ url, label }) => {
    if (!url) return null;
    return (
        <div className="flex items-center gap-2 mt-1 p-2 bg-green-500/10 border border-green-500/30 rounded text-sm">
            <CheckCircle className="w-4 h-4 text-green-400" />
            <span className="text-green-400 flex-1">{label} uploaded</span>
            <Button
                size="sm"
                variant="ghost"
                onClick={() => window.open(getDocumentUrl(url), '_blank')}
                className="text-green-400 hover:text-green-300 h-6 px-2"
            >
                <Download className="w-3 h-3 mr-1" />
                View
            </Button>
        </div>
    );
};

export default function ComplianceTab({ truck, onUpdate }) {
    const [compliance, setCompliance] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState({});
    
    const [formData, setFormData] = useState({
        cdl_number: '',
        cdl_state: '',
        cdl_expiry_date: '',
        med_doc_name: '',
        med_registry_num: '',
        med_exam_date: '',
        coi_provider: '',
        coi_policy_number: '',
        coi_expiry: '',
        mvr_check_date: '',
        drug_test_status: 'pending',
        drug_test_date: ''
    });
    
    // Track uploaded document URLs
    const [docUrls, setDocUrls] = useState({
        cdl_front_url: null,
        cdl_back_url: null,
        med_card_url: null,
        coi_doc_url: null,
        mvr_doc_url: null,
        drug_test_doc_url: null
    });

    // Fetch compliance data
    useEffect(() => {
        const fetchCompliance = async () => {
            if (!truck?.truck_id) return;
            try {
                const res = await fetch(`${API_URL}/api/trucks/${truck.truck_id}/compliance`, {
                    credentials: 'include'
                });
                if (res.ok) {
                    const data = await res.json();
                    setCompliance(data);
                    // Populate form
                    const cd = data.compliance_data || {};
                    setFormData({
                        cdl_number: cd.cdl_number || '',
                        cdl_state: cd.cdl_state || '',
                        cdl_expiry_date: cd.cdl_expiry_date || '',
                        med_doc_name: cd.med_doc_name || '',
                        med_registry_num: cd.med_registry_num || '',
                        med_exam_date: cd.med_exam_date || '',
                        coi_provider: cd.coi_provider || '',
                        coi_policy_number: cd.coi_policy_number || '',
                        coi_expiry: cd.coi_expiry || '',
                        mvr_check_date: cd.mvr_check_date || '',
                        drug_test_status: cd.drug_test_status || 'pending',
                        drug_test_date: cd.drug_test_date || ''
                    });
                    // Populate document URLs
                    setDocUrls({
                        cdl_front_url: cd.cdl_front_url || null,
                        cdl_back_url: cd.cdl_back_url || null,
                        med_card_url: cd.med_card_url || null,
                        coi_doc_url: cd.coi_doc_url || null,
                        mvr_doc_url: cd.mvr_doc_url || null,
                        drug_test_doc_url: cd.drug_test_doc_url || null
                    });
                }
            } catch (e) {
                console.error('Failed to fetch compliance:', e);
            } finally {
                setLoading(false);
            }
        };
        fetchCompliance();
    }, [truck?.truck_id]);

    // Save compliance data
    const handleSave = async () => {
        setSaving(true);
        try {
            const res = await fetch(`${API_URL}/api/trucks/${truck.truck_id}/compliance`, {
                method: 'PATCH',
                headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify(formData)
            });
            
            if (res.ok) {
                const data = await res.json();
                setCompliance(data);
                toast.success('Compliance data saved');
                onUpdate?.();
            } else {
                toast.error('Failed to save compliance data');
            }
        } catch (error) {
            console.error('Save error:', error);
            toast.error('Failed to save');
        } finally {
            setSaving(false);
        }
    };

    // Upload document
    const handleFileUpload = async (docType, file) => {
        if (!file) return;
        
        setUploading(prev => ({ ...prev, [docType]: true }));
        
        const formData = new FormData();
        formData.append('file', file);
        formData.append('doc_type', docType);
        
        try {
            const res = await fetch(`${API_URL}/api/trucks/${truck.truck_id}/compliance/upload`, {
                method: 'POST',
                credentials: 'include',
                body: formData
            });
            
            if (res.ok) {
                const uploadData = await res.json();
                toast.success(`${docType.replace('_', ' ')} uploaded`);
                
                // Update docUrls with the new file path
                const docUrlField = `${docType}_url`.replace('cdl_front', 'cdl_front').replace('cdl_back', 'cdl_back');
                if (uploadData.file_path) {
                    setDocUrls(prev => ({
                        ...prev,
                        [`${docType}_url`]: uploadData.file_path
                    }));
                }
                
                // Refresh compliance data
                const compRes = await fetch(`${API_URL}/api/trucks/${truck.truck_id}/compliance`, {
                    credentials: 'include'
                });
                if (compRes.ok) {
                    const data = await compRes.json();
                    setCompliance(data);
                    // Also update docUrls from refreshed data
                    const cd = data.compliance_data || {};
                    setDocUrls({
                        cdl_front_url: cd.cdl_front_url || null,
                        cdl_back_url: cd.cdl_back_url || null,
                        med_card_url: cd.med_card_url || null,
                        coi_doc_url: cd.coi_doc_url || null,
                        mvr_doc_url: cd.mvr_doc_url || null,
                        drug_test_doc_url: cd.drug_test_doc_url || null
                    });
                }
            } else {
                const err = await res.json();
                toast.error(err.detail || 'Upload failed');
            }
        } catch (error) {
            console.error('Upload error:', error);
            toast.error('Upload failed');
        } finally {
            setUploading(prev => ({ ...prev, [docType]: false }));
        }
    };

    // Check if a field is compliant
    const getFieldStatus = (field) => {
        if (!compliance?.failures) return 'unknown';
        const failure = compliance.failures.find(f => f.field === field);
        return failure ? 'failed' : 'passed';
    };

    const StatusIcon = ({ field }) => {
        const status = getFieldStatus(field);
        if (status === 'passed') return <Check className="w-4 h-4 text-green-400" />;
        if (status === 'failed') return <X className="w-4 h-4 text-red-400" />;
        return <AlertTriangle className="w-4 h-4 text-zinc-500" />;
    };

    if (loading) {
        return (
            <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
            </div>
        );
    }

    return (
        <div className="space-y-6">
            {/* Compliance Status Banner */}
            <Card className={`border-2 ${compliance?.compliant ? 'border-green-500 bg-green-900/20' : 'border-red-500 bg-red-900/20'}`}>
                <CardContent className="py-4">
                    <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                            <Shield className={`w-8 h-8 ${compliance?.compliant ? 'text-green-400' : 'text-red-400'}`} />
                            <div>
                                <h3 className={`font-bold text-lg ${compliance?.compliant ? 'text-green-400' : 'text-red-400'}`}>
                                    {compliance?.compliant ? 'COMPLIANT' : 'NON-COMPLIANT'}
                                </h3>
                                <p className="text-zinc-400 text-sm">
                                    {compliance?.compliant 
                                        ? 'All DOT safety requirements met' 
                                        : `${compliance?.failures?.length || 0} issue(s) require attention`}
                                </p>
                            </div>
                        </div>
                        <Button 
                            variant="outline" 
                            size="sm"
                            onClick={handleSave}
                            disabled={saving}
                            className="border-zinc-700"
                        >
                            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
                            Save Changes
                        </Button>
                    </div>
                    
                    {/* Failures List */}
                    {compliance?.failures?.length > 0 && (
                        <div className="mt-4 space-y-1">
                            {compliance.failures.map((f, idx) => (
                                <div key={idx} className="text-red-400 text-sm flex items-center gap-2">
                                    <X className="w-3 h-3" />
                                    <span>{f.reason}</span>
                                </div>
                            ))}
                        </div>
                    )}
                </CardContent>
            </Card>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {/* CDL Information */}
                <Card className="bg-zinc-800/50 border-zinc-700">
                    <CardHeader className="pb-3">
                        <CardTitle className="text-base flex items-center gap-2">
                            <Car className="w-4 h-4 text-blue-400" />
                            CDL Information
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="text-zinc-500 text-xs">CDL Number</label>
                                <Input
                                    value={formData.cdl_number}
                                    onChange={(e) => setFormData({...formData, cdl_number: e.target.value})}
                                    className="bg-zinc-900 border-zinc-700 text-white mt-1"
                                    placeholder="CDL12345678"
                                />
                            </div>
                            <div>
                                <label className="text-zinc-500 text-xs">State</label>
                                <Input
                                    value={formData.cdl_state}
                                    onChange={(e) => setFormData({...formData, cdl_state: e.target.value.toUpperCase()})}
                                    className="bg-zinc-900 border-zinc-700 text-white mt-1"
                                    placeholder="TX"
                                    maxLength={2}
                                />
                            </div>
                        </div>
                        <div>
                            <label className="text-zinc-500 text-xs">CDL Expiry Date</label>
                            <Input
                                type="date"
                                value={formData.cdl_expiry_date}
                                onChange={(e) => setFormData({...formData, cdl_expiry_date: e.target.value})}
                                className="bg-zinc-900 border-zinc-700 text-white mt-1"
                            />
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="text-zinc-500 text-xs">CDL Front</label>
                                <Input
                                    type="file"
                                    accept=".pdf,.jpg,.jpeg,.png"
                                    onChange={(e) => handleFileUpload('cdl_front', e.target.files[0])}
                                    className="bg-zinc-900 border-zinc-700 text-white mt-1 text-xs"
                                    disabled={uploading.cdl_front}
                                />
                                <UploadedFileIndicator url={docUrls.cdl_front_url} label="CDL Front" />
                            </div>
                            <div>
                                <label className="text-zinc-500 text-xs">CDL Back</label>
                                <Input
                                    type="file"
                                    accept=".pdf,.jpg,.jpeg,.png"
                                    onChange={(e) => handleFileUpload('cdl_back', e.target.files[0])}
                                    className="bg-zinc-900 border-zinc-700 text-white mt-1 text-xs"
                                    disabled={uploading.cdl_back}
                                />
                                <UploadedFileIndicator url={docUrls.cdl_back_url} label="CDL Back" />
                            </div>
                        </div>
                    </CardContent>
                </Card>

                {/* Medical Certification */}
                <Card className="bg-zinc-800/50 border-zinc-700">
                    <CardHeader className="pb-3">
                        <CardTitle className="text-base flex items-center gap-2">
                            <User className="w-4 h-4 text-green-400" />
                            Medical Certification
                            <StatusIcon field="med_exam_date" />
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                        <div>
                            <label className="text-zinc-500 text-xs">Medical Examiner Name</label>
                            <Input
                                value={formData.med_doc_name}
                                onChange={(e) => setFormData({...formData, med_doc_name: e.target.value})}
                                className="bg-zinc-900 border-zinc-700 text-white mt-1"
                                placeholder="Dr. John Smith"
                            />
                        </div>
                        <div>
                            <label className="text-zinc-500 text-xs">National Registry Number</label>
                            <Input
                                value={formData.med_registry_num}
                                onChange={(e) => setFormData({...formData, med_registry_num: e.target.value})}
                                className="bg-zinc-900 border-zinc-700 text-white mt-1"
                                placeholder="1234567890"
                            />
                        </div>
                        <div>
                            <label className="text-zinc-500 text-xs flex items-center gap-2">
                                Exam Date 
                                <Badge variant="outline" className="text-xs">Must be within 24 months</Badge>
                            </label>
                            <Input
                                type="date"
                                value={formData.med_exam_date}
                                onChange={(e) => setFormData({...formData, med_exam_date: e.target.value})}
                                className="bg-zinc-900 border-zinc-700 text-white mt-1"
                            />
                        </div>
                        <div>
                            <label className="text-zinc-500 text-xs">Medical Card Upload</label>
                            <Input
                                type="file"
                                accept=".pdf,.jpg,.jpeg,.png"
                                onChange={(e) => handleFileUpload('med_card', e.target.files[0])}
                                className="bg-zinc-900 border-zinc-700 text-white mt-1 text-xs"
                                disabled={uploading.med_card}
                            />
                            <UploadedFileIndicator url={docUrls.med_card_url} label="Medical Card" />
                        </div>
                    </CardContent>
                </Card>

                {/* Certificate of Insurance */}
                <Card className="bg-zinc-800/50 border-zinc-700">
                    <CardHeader className="pb-3">
                        <CardTitle className="text-base flex items-center gap-2">
                            <FileText className="w-4 h-4 text-purple-400" />
                            Certificate of Insurance (COI)
                            <StatusIcon field="coi_expiry" />
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                        <div>
                            <label className="text-zinc-500 text-xs">Insurance Provider</label>
                            <Input
                                value={formData.coi_provider}
                                onChange={(e) => setFormData({...formData, coi_provider: e.target.value})}
                                className="bg-zinc-900 border-zinc-700 text-white mt-1"
                                placeholder="Progressive Commercial"
                            />
                        </div>
                        <div>
                            <label className="text-zinc-500 text-xs">Policy Number</label>
                            <Input
                                value={formData.coi_policy_number}
                                onChange={(e) => setFormData({...formData, coi_policy_number: e.target.value})}
                                className="bg-zinc-900 border-zinc-700 text-white mt-1"
                                placeholder="POL-123456"
                            />
                        </div>
                        <div>
                            <label className="text-zinc-500 text-xs flex items-center gap-2">
                                Expiry Date
                                <Badge variant="outline" className="text-xs text-red-400">Must not be expired</Badge>
                            </label>
                            <Input
                                type="date"
                                value={formData.coi_expiry}
                                onChange={(e) => setFormData({...formData, coi_expiry: e.target.value})}
                                className="bg-zinc-900 border-zinc-700 text-white mt-1"
                            />
                        </div>
                        <div>
                            <label className="text-zinc-500 text-xs">COI Document</label>
                            <Input
                                type="file"
                                accept=".pdf,.jpg,.jpeg,.png"
                                onChange={(e) => handleFileUpload('coi', e.target.files[0])}
                                className="bg-zinc-900 border-zinc-700 text-white mt-1 text-xs"
                                disabled={uploading.coi}
                            />
                            <UploadedFileIndicator url={docUrls.coi_doc_url} label="COI" />
                        </div>
                    </CardContent>
                </Card>

                {/* MVR Check */}
                <Card className="bg-zinc-800/50 border-zinc-700">
                    <CardHeader className="pb-3">
                        <CardTitle className="text-base flex items-center gap-2">
                            <Calendar className="w-4 h-4 text-amber-400" />
                            Motor Vehicle Record (MVR)
                            <StatusIcon field="mvr_check_date" />
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-3">
                        <div>
                            <label className="text-zinc-500 text-xs flex items-center gap-2">
                                Last MVR Check Date
                                <Badge variant="outline" className="text-xs">Must be within 12 months</Badge>
                            </label>
                            <Input
                                type="date"
                                value={formData.mvr_check_date}
                                onChange={(e) => setFormData({...formData, mvr_check_date: e.target.value})}
                                className="bg-zinc-900 border-zinc-700 text-white mt-1"
                            />
                        </div>
                        <div>
                            <label className="text-zinc-500 text-xs">MVR Document</label>
                            <Input
                                type="file"
                                accept=".pdf,.jpg,.jpeg,.png"
                                onChange={(e) => handleFileUpload('mvr', e.target.files[0])}
                                className="bg-zinc-900 border-zinc-700 text-white mt-1 text-xs"
                                disabled={uploading.mvr}
                            />
                            <UploadedFileIndicator url={docUrls.mvr_doc_url} label="MVR" />
                        </div>
                    </CardContent>
                </Card>

                {/* Drug & Alcohol Testing */}
                <Card className="bg-zinc-800/50 border-zinc-700 md:col-span-2">
                    <CardHeader className="pb-3">
                        <CardTitle className="text-base flex items-center gap-2">
                            <Shield className="w-4 h-4 text-red-400" />
                            Drug & Alcohol Testing
                            <StatusIcon field="drug_test_status" />
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                                <label className="text-zinc-500 text-xs flex items-center gap-2">
                                    Status
                                    <Badge variant="outline" className="text-xs text-green-400">Must be "Verified"</Badge>
                                </label>
                                <Select 
                                    value={formData.drug_test_status} 
                                    onValueChange={(val) => setFormData({...formData, drug_test_status: val})}
                                >
                                    <SelectTrigger className="bg-zinc-900 border-zinc-700 text-white mt-1">
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent className="bg-zinc-900 border-zinc-700">
                                        <SelectItem value="pending">Pending</SelectItem>
                                        <SelectItem value="verified">Verified</SelectItem>
                                        <SelectItem value="failed">Failed</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <label className="text-zinc-500 text-xs">Last Test Date</label>
                                <Input
                                    type="date"
                                    value={formData.drug_test_date}
                                    onChange={(e) => setFormData({...formData, drug_test_date: e.target.value})}
                                    className="bg-zinc-900 border-zinc-700 text-white mt-1"
                                />
                            </div>
                            <div>
                                <label className="text-zinc-500 text-xs">Test Results Document</label>
                                <Input
                                    type="file"
                                    accept=".pdf,.jpg,.jpeg,.png"
                                    onChange={(e) => handleFileUpload('drug_test', e.target.files[0])}
                                    className="bg-zinc-900 border-zinc-700 text-white mt-1 text-xs"
                                    disabled={uploading.drug_test}
                                />
                                <UploadedFileIndicator url={docUrls.drug_test_doc_url} label="Drug Test Results" />
                            </div>
                        </div>
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
